import React, { useRef, useState } from 'react';
import useClickOutside from '../../../Hooks/useClickOutside';
import { useAdminModuleAccess } from '../../../context/AdminModuleAccessContext';
import { ADMIN_PAGE_TITLE_CLASS, ADMIN_COMPACT_FORM_SHELL_CLASS, ADMIN_COMPACT_FORM_BODY_CLASS } from './adminLayoutSpec';
import {
  DashboardHeading,
  DashboardText,
  DashboardAvatar,
  DashboardIcon,
  DashboardStatusLabel,
  DashboardCta,
  DashboardFieldLabel,
  DashboardFieldError,
  DASHBOARD_CTA_CHIP_CLASS,
  dashboardFieldClassNames,
  dashboardInlineFieldClassNames,
  dashboardPaymentFieldClassNames,
  dashboardVerificationDigitClassNames,
  dashboardVerificationGroupClassNames,
  dashboardCtaClassNames,
} from '../../../components/common/Dashboard';

function labelTextFromChildren(children) {
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(labelTextFromChildren).join(' ');
  if (React.isValidElement(children)) return labelTextFromChildren(children.props?.children);
  return '';
}

/** Disable mutating CTAs when module is view-only / missing add or edit. */
function usePermissionAwareDisabled(disabled, children, { allowWhenRestricted = false } = {}) {
  const { canAdd, canEdit, restrictMutations } = useAdminModuleAccess();
  if (disabled) return true;
  if (allowWhenRestricted) return false;
  const label = labelTextFromChildren(children).replace(/\s+/g, ' ').trim();
  const isAddLabel = /^(add|create|new)\b/i.test(label);
  if (isAddLabel) return !canAdd;
  if (restrictMutations || !canEdit) return true;
  return false;
}

/* ── §4 Headings ───────────────────────────────────────────── */

export function AdminPageTitle({ children, className = ADMIN_PAGE_TITLE_CLASS }) {
  return (
    <DashboardHeading variant="page" className={className}>
      {children}
    </DashboardHeading>
  );
}

export function AdminSectionTitle({ children, className = '' }) {
  return (
    <DashboardHeading variant="section" className={className}>
      {children}
    </DashboardHeading>
  );
}

export function AdminWidgetTitle({ children, className = '' }) {
  return (
    <DashboardHeading variant="widget" className={className}>
      {children}
    </DashboardHeading>
  );
}

export function AdminPanelTitle({ children, className = '' }) {
  return (
    <DashboardHeading variant="panel" className={className}>
      {children}
    </DashboardHeading>
  );
}

/** Admin dashboard spec §4 — sm 12px · regular 14px · lg 16px (fixed, no breakpoint scaling). */
export function AdminIcon({ children, size = 'regular', className = '', ...rest }) {
  const dashboardSize = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'regular';
  return (
    <DashboardIcon size={dashboardSize} className={className} {...rest}>
      {children}
    </DashboardIcon>
  );
}

/** Filter dropdown chevron — regular 14px. */
export function AdminFilterChevron({ open, className = '' }) {
  return (
    <AdminIcon className={`shrink-0 ${className}`.trim()}>
      <i className={open ? 'fi fi-rs-angle-up' : 'fi fi-rs-angle-down'} aria-hidden />
    </AdminIcon>
  );
}

/** Form detail select trigger chevron — regular 14px, positioned inside dropdown fields. */
export function AdminFormDropdownChevron({ open = false, disabled = false, className = '' }) {
  const chevronOpen = open && !disabled;
  const colorClass = disabled ? 'text-mediumgray' : 'text-offblack';
  return (
    <span
      className={`pointer-events-none absolute right-2.5 top-1/2 z-[5] flex h-3.5 w-3.5 shrink-0 -translate-y-1/2 items-center justify-center ${className}`.trim()}
      aria-hidden
    >
      <AdminFilterChevron open={chevronOpen} className={colorClass} />
    </span>
  );
}

/** Table row actions menu — regular 14px. */
export function AdminRowMenuIcon({ className = '' }) {
  return (
    <AdminIcon className={className}>
      <i className="fi fi-rs-menu-dots-vertical text-offblack" aria-hidden />
    </AdminIcon>
  );
}

/** Admin dashboard spec — header close (text-sm / 14px). */
export function AdminHeaderCloseIcon({
  iconClass = 'fi fi-rs-cross text-sm leading-none',
  className = '',
  ...rest
}) {
  return (
    <i className={`${iconClass} ${className}`.trim()} aria-hidden {...rest} />
  );
}

/** Filter / toolbar group label — 12px / 500 (Typography §4 small label heading). */
export function AdminFilterGroupLabel({ children, className = '', htmlFor, as = 'span' }) {
  if (as === 'label') {
    return (
      <label htmlFor={htmlFor} className={`dashboard-label-heading text-offblack ${className}`.trim()}>
        {children}
      </label>
    );
  }
  return (
    <DashboardHeading variant="label" as={as} className={className}>
      {children}
    </DashboardHeading>
  );
}

/* ── §5–§11 Text ───────────────────────────────────────────── */

export function AdminMainText({ children, className = '', as = 'span' }) {
  return (
    <DashboardText variant="main" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

export function AdminSupportingText({ children, className = '', as = 'p' }) {
  return (
    <DashboardText variant="supporting" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

export function AdminMetadataText({ children, className = '', as = 'span' }) {
  return (
    <DashboardText variant="metadata" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

export function AdminAlertText({ children, className = '', as = 'p' }) {
  return (
    <DashboardText variant="alert" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

/** Alert detail — 12px / 400 (Typography §10). */
export function AdminAlertSupportingText({ children, className = '', as = 'p' }) {
  return (
    <DashboardText variant="alert-supporting" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

/** Sidebar / top nav — 12px lg · 13px xl / 500 (Typography §8). */
export function AdminNavText({ children, className = '', as = 'span' }) {
  return (
    <DashboardText variant="nav" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

/** Lookup matched term — 12px / 500 (Typography §11). */
export function AdminAutocompleteHighlight({ children, className = '', as = 'span' }) {
  return (
    <DashboardText variant="autocomplete-highlight" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

/* ── §9 Modal ──────────────────────────────────────────────── */

export function AdminModalTitle({ children, className = '', as = 'h2' }) {
  return (
    <DashboardText variant="modal-title" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

export function AdminModalBody({ children, className = '', as = 'p' }) {
  return (
    <DashboardText variant="modal-body" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

export function AdminModalMeta({ children, className = '', as = 'span' }) {
  return (
    <DashboardText variant="modal-meta" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

/* ── §7 Table ──────────────────────────────────────────────── */

export function AdminTableHeader({ children, className = '' }) {
  return (
    <DashboardText variant="table-header" as="span" className={className}>
      {children}
    </DashboardText>
  );
}

export function AdminTableBody({ children, className = '', emphasis = false }) {
  return (
    <DashboardText
      variant={emphasis ? 'table-body-emphasis' : 'table-body'}
      as="span"
      className={className}
    >
      {children}
    </DashboardText>
  );
}

export function AdminTableMeta({ children, className = '' }) {
  return (
    <DashboardText variant="table-meta" as="span" className={`tabular-nums ${className}`.trim()}>
      {children}
    </DashboardText>
  );
}

export function AdminSortableTableHeader({ children, onClick, className = '' }) {
  return (
    <button
      type="button"
      data-admin-sort=""
      onClick={onClick}
      className={`flex items-center gap-1 hover:text-primaryblue ${className}`.trim()}
    >
      <AdminTableHeader>{children}</AdminTableHeader>
    </button>
  );
}

/* ── §6 Metrics ────────────────────────────────────────────── */

export function AdminMetric({ size = 'medium', children, className = '' }) {
  const variant =
    size === 'large'
      ? 'metric-large'
      : size === 'small'
        ? 'metric-small'
        : size === 'micro'
          ? 'metric-micro'
          : 'metric-medium';
  return (
    <DashboardText variant={variant} as="span" className={`tabular-nums ${className}`.trim()}>
      {children}
    </DashboardText>
  );
}

/** Metric micro + icon — §6 metric micro (12px / 400); not a status label or CTA. */
export function AdminMetricDelta({ value, className = '' }) {
  const isUp = value >= 0;
  return (
    <span
      className={`flex items-center gap-1 mt-1 ${isUp ? 'text-freshgreen' : 'text-alertred'} ${className}`.trim()}
    >
      <DashboardIcon size="sm" className={isUp ? 'text-freshgreen' : 'text-alertred'}>
        <i
          className={`fi fi-sr-triangle flex items-center justify-center ${isUp ? 'rotate-0' : 'rotate-180'}`}
          aria-hidden
        />
      </DashboardIcon>
      <AdminMetric size="micro">
        {isUp ? '+' : ''}
        {value}%
      </AdminMetric>
    </span>
  );
}

/** Inline “label: value” line in cards */
export function AdminStatLine({ label, children, className = '' }) {
  return (
    <AdminSupportingText className={className}>
      {label}{' '}
      <AdminTableBody emphasis className="text-offblack">
        {children}
      </AdminTableBody>
    </AdminSupportingText>
  );
}

export const QUOTE_DISPLAY_STATUSES = ['Active', 'Under Review', 'Blocked', 'Restricted'];
/** Parent Message operational statuses (Admin Messages Screen 1 / Overview). */
export const MESSAGE_DISPLAY_STATUSES = ['Active', 'Under Review', 'Blocked', 'Restricted'];
/** Latest Message / Quote Thread delivery statuses. */
export const MESSAGE_DELIVERY_STATUSES = ['Sent', 'Delivered', 'Read'];
/** Alias for Quote Thread delivery status (Sent / Delivered / Read). */
export const QUOTE_THREAD_DELIVERY_STATUSES = MESSAGE_DELIVERY_STATUSES;

/**
 * Derive parent Message operational status.
 * Priority: Under Review (active Medium/High case) → Blocked → Restricted → Active.
 */
export function deriveMessageThreadStatus({
  status,
  messagingState,
  underReview,
  caseStatus,
  caseSeverity,
} = {}) {
  const state = String(messagingState || '').trim();
  const s = String(status || '').trim();
  const reviewStatus = String(caseStatus || '').trim();
  const severity = String(caseSeverity || '').trim();
  const hasActiveReview =
    underReview === true ||
    reviewStatus === 'Under Review' ||
    reviewStatus === 'In Review' ||
    reviewStatus === 'New' ||
    reviewStatus === 'Active' ||
    s === 'Under Review' ||
    s === 'In Review';
  const isMediumOrHigh = severity === 'Medium' || severity === 'High' || severity === '';

  // Medium/High New or Active cases pause sending → parent Under Review. Low alone does not.
  if (hasActiveReview && (severity === '' || isMediumOrHigh) && severity !== 'Low') {
    return 'Under Review';
  }
  if (state === 'Blocked' || s === 'Blocked') return 'Blocked';
  if (state === 'Restricted' || s === 'Restricted') return 'Restricted';
  if (MESSAGE_DISPLAY_STATUSES.includes(s)) return s;
  // Legacy list/detail values map to Active (messaging available).
  if (
    s === 'New' ||
    s === 'Open' ||
    s === 'Closed' ||
    s === 'Submitted' ||
    s === 'Active' ||
    s === 'active' ||
    s === 'Accepted' ||
    s === 'Responded'
  ) {
    return 'Active';
  }
  return 'Active';
}

/** Normalize parent Message operational status for admin UI. */
export function normalizeMessageStatus(status, messagingState, messages, extras = {}) {
  return deriveMessageThreadStatus({
    status,
    messagingState,
    messages,
    underReview: extras.underReview,
    caseStatus: extras.caseStatus,
    caseSeverity: extras.caseSeverity,
  });
}

/** Normalize latest Message Thread delivery status (Sent / Delivered / Read). */
export function normalizeMessageDeliveryStatus(status) {
  const s = String(status || '').trim();
  if (MESSAGE_DELIVERY_STATUSES.includes(s)) return s;
  if (s === 'Unread') return 'Delivered';
  return 'Sent';
}

/**
 * Normalize parent Quote operational status for admin UI.
 * Same priority as Messages: Under Review → Blocked → Restricted → Active.
 */
export function normalizeQuoteStatus(status, threadStatus, extras = {}) {
  const s = String(status || '').trim();
  const t = String(threadStatus || '').trim();
  return deriveMessageThreadStatus({
    status: s || t,
    messagingState: extras.messagingState,
    underReview: extras.underReview,
    caseStatus: extras.caseStatus || (t === 'Under Review' || t === 'In Review' ? 'Under Review' : undefined),
    caseSeverity: extras.caseSeverity,
  });
}

/** Normalize Quote Thread delivery status (Sent / Delivered / Read). */
export function normalizeQuoteThreadDeliveryStatus(status) {
  return normalizeMessageDeliveryStatus(status);
}

/* ── Status labels — colour map (Avatar/Icon/Status system) ─── */

const DEFAULT_STATUS_CLASS = 'bg-mediumgray text-white';

export function adminStatusBadgeClass(status) {
  const s = status == null ? '' : String(status).trim();
  if (s === 'Active' || s === 'Healthy' || s === 'Published' || s === 'Connected' || s === 'Verified')
    return 'bg-freshgreen text-white';
  if (s === 'Not Verified') return 'bg-lightgray text-offblack';
  if (s === 'Warning') return 'bg-warmyellow text-offblack';
  if (s === 'New' || s === 'Improving') return 'bg-primaryblue text-white';
  if (s === 'Under Review' || s === 'Watch' || s === 'Pending' || s === 'Verification Pending' || s === 'Validation Pending')
    return 'bg-warmyellow text-offblack';
  if (s === 'In Review' || s === 'Suspended' || s === 'Stable' || s === 'Hidden')
    return 'bg-mediumgray text-white';
  if (s === 'Evaluating') return 'bg-freshgreen text-white';
  if (
    s === 'Not Evaluating — Rule Inactive' ||
    s === 'Suspended — Target Form Inactive' ||
    s === 'Suspended — Target Field Inactive'
  )
    return 'bg-mediumgray text-white';
  if (s === 'Restricted' || s === 'Action Needed' || s === 'Pending Review')
    return 'bg-softpurple text-white';
  if (s === 'At Risk' || s === 'Banned' || s === 'Blocked' || s === 'Closed' || s === 'Flagged')
    return 'bg-alertred text-white';
  if (s === 'Deleted' || s === 'Inactive' || s === 'Removed' || s === 'Deactivated')
    return 'bg-offblack text-white';
  if (s === 'Pending Access') return 'bg-warmyellow text-offblack';
  if (s === 'Password Reset Required') return 'bg-softpurple text-white';
  if (s === 'Review Required') return 'bg-warmyellow text-offblack';
  if (s === 'Source Inactive') return 'bg-softpurple text-white';
  if (s === 'Open' || s === 'In Progress') return 'bg-freshgreen text-white';
  if (s === 'Closed') return 'bg-mediumgray text-white';
  if (s === 'Archived') return 'bg-lightgray text-offblack';
  if (s === 'Blocked' || s === 'Expired') return 'bg-alertred text-white';
  if (s === 'Submitted') return 'bg-primaryblue text-white';
  if (s === 'Approved' || s === 'Visible' || s === 'No Breach Found') return 'bg-freshgreen text-white';
  if (s === 'Approved — Renewal Pending') return 'bg-primaryblue text-white';
  if (s === 'Withdrawn') return 'bg-mediumgray text-white';
  if (s === 'Rejected' || s === 'Not Approved') return 'bg-alertred text-white';
  if (s === 'Temporary Ban' || s === 'Permanent Ban' || s === 'Final Warning') return 'bg-alertred text-white';
  if (s === 'Insufficient Evidence' || s === 'Transfer') return 'bg-warmyellow text-offblack';
  if (s === 'No Record') return 'bg-mediumgray text-white';
  if (
    s === 'Resubmission' ||
    s === 'Resubmission Required' ||
    s === 'Awaiting Resubmission' ||
    s === 'Information Requested' ||
    s === 'Awaiting Information'
  )
    return 'bg-primaryblue text-white';
  if (s === 'Expiry Review') return 'bg-warmyellow text-offblack';
  if (s === 'Pending Evidence') return 'bg-softpurple text-white';
  if (s === 'Ready for Decision') return 'bg-brightblue text-white';
  if (s === 'Actioned') return 'bg-freshgreen text-white';
  if (s === 'Escalated') return 'bg-alertred text-white';
  if (s === 'Severe') return 'bg-alertred text-white';
  if (s === 'Low') return 'bg-freshgreen text-white';
  if (s === 'Medium') return 'bg-warmyellow text-offblack';
  if (s === 'High' || s === 'Critical') return 'bg-alertred text-white';
  if (
    s === 'Awaiting Reply' ||
    s === 'Waiting on Customer' ||
    s === 'Waiting on Business' ||
    s === 'Waiting on Visitor'
  )
    return 'bg-warmyellow text-offblack';
  if (s === 'Waiting on Assigned') return 'bg-softpurple text-white';
  if (s === 'Resolved' || s === 'Decided') return 'bg-freshgreen text-white';
  if (s === 'Published') return 'bg-freshgreen text-white';
  if (s === 'Draft' || s === 'In Revision') return 'bg-warmyellow text-offblack';
  if (s === 'Draft Changes') return 'bg-warmyellow text-offblack';
  if (s === 'Not Applied') return 'bg-primaryblue text-white';
  if (s === 'Never Applied') return 'bg-mediumgray text-white';
  if (s === 'Waiting to Apply') return 'bg-primaryblue text-white';
  if (s === 'Not Saved') return 'bg-mediumgray text-white';
  if (s === 'Source Updated') return 'bg-warmyellow text-offblack';
  if (s === 'Pending Application') return 'bg-primaryblue text-white';
  if (s === 'Changes Pending' || s === 'Pending Changes') return 'bg-warmyellow text-offblack';
  if (s === 'Applied') return 'bg-freshgreen text-white';
  if (s === 'Scheduled') return 'bg-primaryblue text-white';
  if (s === 'Cancelled') return 'bg-mediumgray text-white';
  if (s === 'Investigating' || s === 'Testing') return 'bg-warmyellow text-offblack';
  if (s === 'Mitigated' || s === 'Disabled') return 'bg-mediumgray text-white';
  if (s === 'Deprecated') return 'bg-lightgray text-offblack';
  if (s === 'Deleted') return 'bg-alertred text-white';
  if (
    s === 'Success' ||
    s === 'Succeeded' ||
    s === 'Completed' ||
    s === 'Delivered' ||
    s === 'Processed' ||
    s === 'Won' ||
    s === 'Paid' ||
    s === 'Recovered'
  )
    return 'bg-freshgreen text-white';
  if (s === 'Pro') return 'bg-primaryblue text-white';
  if (s === 'Basic') return 'bg-offblack text-white';
  if (s === 'Configuration Unavailable') return 'bg-warmyellow text-offblack';
  if (s === 'Error' || s === 'Failed' || s === 'Denied' || s === 'Lost' || s === 'Degraded')
    return 'bg-alertred text-white';
  if (s === 'Refunded' || s === 'Partially Refunded') return 'bg-softpurple text-white';
  if (s === 'Disputed') return 'bg-alertred text-white';
  if (s === 'Trial') return 'bg-primaryblue text-white';
  if (s === 'Grace Period' || s === 'Action Required') return 'bg-warmyellow text-offblack';
  if (s === 'No Payment Yet' || s === 'Prevented') return 'bg-mediumgray text-white';
  if (s === 'Replaced') return 'bg-mediumgray text-white';
  if (s === 'Expired') return 'bg-warmyellow text-offblack';
  if (s === 'Invalidated') return 'bg-alertred text-white';
  if (s === 'Lifted') return 'bg-mediumgray text-white';
  return DEFAULT_STATUS_CLASS;
}

export function messageThreadStatusBadgeClass(status) {
  const s = String(status || '').trim();
  if (s === 'Active' || s === 'New') return 'bg-freshgreen text-white';
  if (s === 'Under Review') return 'bg-warmyellow text-offblack';
  if (s === 'Restricted') return 'bg-softpurple text-white';
  if (s === 'Blocked' || s === 'Closed') return 'bg-alertred text-white';
  return adminStatusBadgeClass(status);
}

/** Delivery status badge for latest Message Thread (Sent / Delivered / Read). */
export function messageDeliveryStatusBadgeClass(status) {
  const s = String(status || '').trim();
  if (s === 'Read') return 'bg-freshgreen text-white';
  if (s === 'Delivered') return 'bg-primaryblue text-white';
  if (s === 'Sent') return 'bg-warmyellow text-offblack';
  return adminStatusBadgeClass(status);
}

/**
 * WhatsApp-style delivery ticks for chat bubbles.
 * Sent = single grey tick; Delivered = double grey; Read = double blue.
 */
export function MessageDeliveryTicks({ status, className = '' }) {
  const s = normalizeMessageDeliveryStatus(status);
  const isRead = s === 'Read';
  const showDouble = s === 'Delivered' || isRead;
  const colorClass = isRead ? 'text-primaryblue' : 'text-mediumgray';

  return (
    <span
      className={`inline-flex items-center shrink-0 ${colorClass} ${className}`.trim()}
      title={s}
      aria-label={s}
    >
      {showDouble ? (
        <span className="relative inline-block w-[14px] h-[11px]" aria-hidden>
          <i className="fi fi-rr-check text-[10px] leading-none absolute left-0 top-0" />
          <i className="fi fi-rr-check text-[10px] leading-none absolute left-[5px] top-0" />
        </span>
      ) : (
        <i className="fi fi-rr-check text-[10px] leading-none" aria-hidden />
      )}
    </span>
  );
}

export function AdminStatusBadge({ status, label, className = '' }) {
  const text = label ?? status ?? '—';
  return (
    <DashboardStatusLabel className={`whitespace-nowrap ${adminStatusBadgeClass(status)} ${className}`.trim()}>
      {text}
    </DashboardStatusLabel>
  );
}

export function AdminPeriodBadge({ label }) {
  return <AdminStatusBadge status={label} label={label} className="bg-offblack text-white" />;
}

/** Ticket thread channel pill (Email / Web) — Status Label § (10px / 500). */
export function AdminChannelBadge({ label, onSupport = false, className = '' }) {
  const surface = onSupport
    ? 'bg-white/35 text-white ring-1 ring-white/40 border-0'
    : 'bg-white text-offblack border border-lightgray shadow-sm';
  return (
    <DashboardStatusLabel className={`${surface} ${className}`.trim()}>
      {label}
    </DashboardStatusLabel>
  );
}

/* ── §3 CTAs & fields ──────────────────────────────────────── */

/** Quick-action chip — CTA Chip 26px (CTA §4). Not for list filter dropdowns. */
export function AdminFilterChipButton({ children, className = '', ...rest }) {
  return (
    <button
      type="button"
      data-admin-allow-view-only=""
      className={`${DASHBOARD_CTA_CHIP_CLASS} whitespace-nowrap disabled:opacity-50 ${className}`.trim()}
      {...rest}
    >
      {children}
    </button>
  );
}

/** List/toolbar filter dropdown trigger — Secondary CTA § (default `lg` admin list size). */
export function AdminFilterDropdownButton({ children, size = 'lg', className = '', ...rest }) {
  const { className: restClassName, ...buttonRest } = rest;
  const mergedClassName = [
    adminFilterDropdownTriggerClass(size, className),
    restClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" data-admin-allow-view-only="" className={mergedClassName} {...buttonRest}>
      {children}
    </button>
  );
}

/** Alias — CTA chip token via DashboardCta. */
export function AdminCtaChip(props) {
  return <DashboardCta variant="chip" {...props} />;
}

export function AdminFilterMenuItem({ children, active, className = '', ...rest }) {
  return (
    <button
      type="button"
      data-admin-allow-view-only=""
      className={`block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left ${
        active ? 'text-primaryblue' : 'text-offblack'
      } ${className}`.trim()}
      {...rest}
    >
      <AdminTableBody>{children}</AdminTableBody>
    </button>
  );
}

/** List toolbar filter menu panel — matches Configuration Search & Matching. */
export const ADMIN_LIST_FILTER_MENU_CLASS =
  'absolute z-20 mt-1 right-0 min-w-full w-max max-w-[16rem] bg-white border border-lightgray rounded-xl shadow-lg py-1 max-h-56 overflow-y-auto';

/** Map string option lists so `All` shows a specific empty-state label. */
export function toAdminListFilterOptions(values, allLabel = 'All') {
  return values.map((value) => ({
    value,
    label: value === 'All' ? allLabel : value,
  }));
}

/** Complete list-toolbar filter dropdown (trigger + selected label + menu). */
export function AdminListFilterDropdown({
  label,
  value,
  options,
  onChange,
  menuRef,
  open,
  setOpen,
  size = 'lg',
}) {
  const selected = options.find((option) => option.value === value) || options[0];
  const triggerLabel = selected?.label === 'All' ? label : selected?.label || label;
  return (
    <div className="relative" ref={menuRef} data-admin-allow-view-only="">
      <AdminFilterDropdownButton
        size={size}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={label}
      >
        <span className="truncate">{triggerLabel}</span>
        <AdminFilterChevron open={open} />
      </AdminFilterDropdownButton>
      {open && (
        <div className={ADMIN_LIST_FILTER_MENU_CLASS}>
          {options.map((opt) => (
            <AdminFilterMenuItem
              key={String(opt.value ?? 'all')}
              active={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </AdminFilterMenuItem>
          ))}
        </div>
      )}
    </div>
  );
}

/** List toolbar multi-select filter dropdown — same trigger/menu style, stays open on toggle. */
export function AdminMultiSelectFilterDropdown({
  label,
  emptyLabel,
  selected = [],
  options,
  onToggle,
  menuRef,
  open,
  setOpen,
  size = 'lg',
}) {
  const display =
    selected.length === 0
      ? emptyLabel || label
      : selected.length === 1
        ? selected[0]
        : `${selected[0]} +${selected.length - 1}`;

  return (
    <div className="relative" ref={menuRef} data-admin-allow-view-only="">
      <AdminFilterDropdownButton
        size={size}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={label}
      >
        <span className="truncate">{display}</span>
        <AdminFilterChevron open={open} />
      </AdminFilterDropdownButton>
      {open && (
        <div className={ADMIN_LIST_FILTER_MENU_CLASS}>
          {options.map((opt) => (
            <AdminFilterMenuItem
              key={opt}
              active={selected.includes(opt)}
              onClick={() => onToggle(opt)}
            >
              {opt}
            </AdminFilterMenuItem>
          ))}
        </div>
      )}
    </div>
  );
}

/** Table row ⋮ menu item — compact list row (not a full-screen popup CTA). */
export function AdminRowMenuItem({ children, active, className = '', ...rest }) {
  const { restrictMutations } = useAdminModuleAccess();
  const label = labelTextFromChildren(children).replace(/\s+/g, ' ').trim();
  const isViewNav = /^(view|edit|open|details?|see\s+details?|view\s+details?)$/i.test(label);
  const disabled = Boolean(rest.disabled) || (restrictMutations && !isViewNav);

  return (
    <button
      type="button"
      className={`block w-full px-3 py-1.5 text-left hover:text-primaryblue disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-offblack ${
        active ? 'text-primaryblue' : 'text-offblack'
      } ${className}`.trim()}
      {...rest}
      disabled={disabled}
      onClick={disabled ? undefined : rest.onClick}
    >
      <AdminTableBody>{children}</AdminTableBody>
    </button>
  );
}

/** Small admin dialog / assign modal width — not full-screen popup forms. */
export const ADMIN_DIALOG_PANEL_CLASS = 'w-[320px] sm:w-[380px]';

/** Inline ⋮ menu anchored under row trigger. */
export const ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS =
  'absolute right-0 top-full mt-1 z-30 min-w-[200px] w-max max-w-[16rem] rounded-lg border border-lightgray bg-white py-2 shadow-lg';

export function AdminSearchField({ className = '', inputClassName = '', icon = true, ...rest }) {
  return (
    <div className={`relative w-full ${className}`.trim()} data-admin-allow-view-only="">
      <input
        className={`${adminSearchFieldClasses()} !pl-10 ${inputClassName}`.trim()}
        {...rest}
      />
      {icon && (
        <span
          className="pointer-events-none absolute left-3 inset-y-0 flex items-center justify-center"
          aria-hidden
        >
          <AdminIcon className="leading-none">
            <i className="fi fi-rs-search text-offblack leading-none" aria-hidden />
          </AdminIcon>
        </span>
      )}
    </div>
  );
}

const FILTER_DROPDOWN_SIZES = new Set(['micro', 'short', 'small', 'medium', 'lg', 'md', 'sm']);

const FILTER_FIELD_SIZE_TO_CTA = {
  micro: 'sm',
  short: 'md',
  small: 'sm',
  medium: 'lg',
  lg: 'lg',
  md: 'md',
  sm: 'sm',
};

function resolveFilterDropdownSize(sizeOrExtra, extra) {
  if (FILTER_DROPDOWN_SIZES.has(sizeOrExtra)) {
    return { size: sizeOrExtra, classExtra: extra };
  }
  return { size: 'lg', classExtra: sizeOrExtra || '' };
}

/**
 * Form/detail dropdown trigger — Field §3 (4px radius). Use inside config editors, not list toolbars.
 */
export function adminFormDropdownTriggerClass(sizeOrExtra = 'short', extra = '') {
  const { size, classExtra } = resolveFilterDropdownSize(sizeOrExtra, extra);
  const gapClass = size === 'micro' ? 'gap-1' : 'gap-2';

  return [
    dashboardFieldClassNames(size),
    `justify-between ${gapClass} cursor-pointer hover:border-primaryblue`,
    classExtra,
  ]
    .join(' ')
    .trim();
}

/**
 * Toolbar filter dropdown trigger — Secondary CTA pill + spacing (default `lg` admin list size).
 * Legacy: `adminFilterDropdownTriggerClass('extra-classes')` still works (treated as lg + extra).
 */
export function adminFilterDropdownTriggerClass(sizeOrExtra = 'lg', extra = '') {
  const { size, classExtra } = resolveFilterDropdownSize(sizeOrExtra, extra);
  const ctaSize = FILTER_FIELD_SIZE_TO_CTA[size] || 'md';
  const gapClass = size === 'micro' ? 'gap-1' : 'gap-2';

  return [
    dashboardCtaClassNames({ variant: 'secondary', size: ctaSize }),
    `justify-between ${gapClass} cursor-pointer`,
    classExtra,
  ]
    .join(' ')
    .trim();
}

/** Date input inside filter menus — Inline 160, full width of menu. */
export function adminFilterDateInputClasses(extra = '') {
  return `${adminInlineInputClasses('160')} !w-full ${extra}`.trim();
}

/** Toolbar / filter native select — Inline 160. */
export function AdminToolbarSelect({ className = '', ...rest }) {
  return (
    <select
      data-admin-allow-view-only=""
      className={`${adminInlineInputClasses('160')} cursor-pointer ${className}`.trim()}
      {...rest}
    />
  );
}

/** Inline toolbar label — alias of `AdminFilterGroupLabel` with flex row layout. */
export function AdminToolbarLabel({ children, className = '', htmlFor }) {
  return (
    <AdminFilterGroupLabel
      htmlFor={htmlFor}
      as="label"
      className={`flex items-center gap-2 ${className}`.trim()}
    >
      {children}
    </AdminFilterGroupLabel>
  );
}

export function AdminLinkAction({ className = '', allowWhenRestricted = false, disabled, children, ...props }) {
  const permissionDisabled = usePermissionAwareDisabled(disabled, children, { allowWhenRestricted });
  return (
    <DashboardCta
      variant="link"
      className={className}
      {...props}
      disabled={permissionDisabled}
    >
      {children}
    </DashboardCta>
  );
}

/** Table / record row — Small primary (30px). */
export function AdminRowAction({ className = '', allowWhenRestricted = false, disabled, children, ...props }) {
  const permissionDisabled = usePermissionAwareDisabled(disabled, children, { allowWhenRestricted });
  return (
    <DashboardCta
      variant="primary"
      size="sm"
      className={className}
      {...props}
      disabled={permissionDisabled}
    >
      {children}
    </DashboardCta>
  );
}

/** Page header / form save — Large primary (admin spec); optional `size` sm | md | lg. */
export function AdminHeaderAction({
  size = 'lg',
  className = '',
  allowWhenRestricted = false,
  disabled,
  children,
  ...props
}) {
  const permissionDisabled = usePermissionAwareDisabled(disabled, children, { allowWhenRestricted });
  return (
    <DashboardCta
      variant="primary"
      size={size}
      className={className}
      {...props}
      disabled={permissionDisabled}
    >
      {children}
    </DashboardCta>
  );
}

/** Form supporting action — Large secondary (admin spec); optional `size` sm | md | lg. */
export function AdminFormSecondaryAction({
  size = 'lg',
  className = '',
  allowWhenRestricted = false,
  disabled,
  children,
  ...props
}) {
  const permissionDisabled = usePermissionAwareDisabled(disabled, children, { allowWhenRestricted });
  return (
    <DashboardCta
      variant="secondary"
      size={size}
      className={className}
      {...props}
      disabled={permissionDisabled}
    >
      {children}
    </DashboardCta>
  );
}

/** Compact add/edit page — 25rem, stacked fields, Save inside the form. */
export function AdminCompactFormPage({
  title,
  onSubmit,
  children,
  beforeForm = null,
  afterForm = null,
  saveLabel = 'Save',
  showSave = true,
}) {
  return (
    <div className={ADMIN_COMPACT_FORM_SHELL_CLASS}>
      <div className="mb-6">
        <AdminPageTitle className="!mb-0">{title}</AdminPageTitle>
      </div>
      {beforeForm}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (onSubmit) onSubmit(e);
        }}
        className={ADMIN_COMPACT_FORM_BODY_CLASS}
      >
        {children}
        {showSave ? (
          <div className="flex items-center justify-end gap-3">
            <AdminHeaderAction type="submit">{saveLabel}</AdminHeaderAction>
          </div>
        ) : null}
      </form>
      {afterForm}
    </div>
  );
}

/** Primary panel action only — Large primary (36px) (CTA §3). */
export function AdminLargeAction({
  className = '',
  allowWhenRestricted = false,
  disabled,
  children,
  ...props
}) {
  const permissionDisabled = usePermissionAwareDisabled(disabled, children, { allowWhenRestricted });
  return (
    <DashboardCta
      variant="primary"
      size="lg"
      className={className}
      {...props}
      disabled={permissionDisabled}
    >
      {children}
    </DashboardCta>
  );
}

/** Inline navigation — Link CTA §5 (12px / 400, 500 hover). */
export function AdminNavShortcut({ to, children, className = '' }) {
  return (
    <AdminLinkAction to={to} className={className}>
      {children}
    </AdminLinkAction>
  );
}

/** Table / queue row — 24px (Avatar §3 small). */
export function AdminRowAvatar({ src, alt = '', initials, className = '', onError }) {
  return (
    <DashboardAvatar
      size="sm"
      src={src}
      alt={alt}
      initials={initials}
      onError={onError}
      className={`bg-mistygray ${className}`.trim()}
    />
  );
}

/** Panel row / record — 32px standard (Avatar §3). */
export function AdminStandardAvatar({ src, alt = '', initials, className = '', onError }) {
  return (
    <DashboardAvatar
      size="md"
      src={src}
      alt={alt}
      initials={initials}
      onError={onError}
      className={`bg-mistygray ${className}`.trim()}
    />
  );
}

export function AdminRowActionsButton({ children, className = '', ...rest }) {
  return (
    <button
      type="button"
      className={`w-8 h-8 rounded-full hover:bg-mistygray flex items-center justify-center transition-colors ${className}`.trim()}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Fixed-position dropdown panel for table row ⋮ menus (avoids overflow scroll on last row). */
export function AdminFixedRowActionsMenu({ layout, menuRef, children, className = '', placementReady = true }) {
  if (!layout) return null;
  return (
    <div
      ref={menuRef}
      className={`fixed z-[200] min-w-[200px] w-max max-w-[16rem] rounded-lg border border-lightgray bg-white py-2 shadow-lg ${
        placementReady ? '' : 'invisible pointer-events-none'
      } ${className}`.trim()}
      style={{ top: layout.top, right: layout.right }}
    >
      {children}
    </div>
  );
}

/* ── §8 Pagination ─────────────────────────────────────────── */

export function AdminPaginationText({ children, className = '', as = 'span' }) {
  return (
    <DashboardText variant="pagination" as={as} className={className}>
      {children}
    </DashboardText>
  );
}

export function AdminPaginationEllipsis() {
  return (
    <DashboardText variant="pagination" as="span" className="px-2 text-mediumgray">
      ...
    </DashboardText>
  );
}

/** Pagination control — §8 pagination UI (12px / 500), not CTA system. */
export function AdminPaginationNavButton({ children, active, className = '', ...rest }) {
  const base =
    'dashboard-pagination-control border border-mediumgray bg-white text-offblack rounded hover:border-primaryblue disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-mediumgray';
  const activeClass = active
    ? '!bg-brightblue !text-white !border-brightblue hover:!border-brightblue'
    : '';
  return (
    <button type="button" className={`${base} ${activeClass} ${className}`.trim()} {...rest}>
      {children}
    </button>
  );
}

function stripAdminNoteRoleLabel(value) {
  if (!value) return '';
  return String(value)
    .replace(/^(Admin|Compliance|Support|Moderator|Fraud\s*&\s*Security) — /i, '')
    .replace(/^Security Ops:\s*/i, '')
    .replace(/^Admin: /, '')
    .replace(/^Admin\s*[—:]\s*/i, '')
    .split(' (')[0]
    .trim();
}

function looksLikeInitialsOnly(value) {
  const v = String(value || '').trim();
  if (!v) return false;
  if (/^[A-Z](\.[A-Z]\.?)+$/.test(v)) return true;
  if (/^Admin-\d+$/i.test(v)) return true;
  return false;
}

/** Full display name for Admin Notes "Added By" (prefer parsed adminName over initials). */
export function getAdminNoteAddedByDisplay(note = {}) {
  const parsedFromFields = [note.adminName, note.addedBy, note.createdBy]
    .map(stripAdminNoteRoleLabel)
    .filter(Boolean);

  const parsedFullName = parsedFromFields.find((name) => !looksLikeInitialsOnly(name));
  if (parsedFullName) return parsedFullName;

  const author = stripAdminNoteRoleLabel(note.author);
  if (author && !looksLikeInitialsOnly(author)) return author;

  return (
    parsedFromFields[0] ||
    author ||
    note.adminName ||
    note.addedBy ||
    note.createdBy ||
    note.author ||
    '—'
  );
}

/** Standalone "Escalated" rows are excluded from queue/detail audit log tables. */
export function isEscalatedAuditLogEntry(log) {
  const action = String(log?.action || log?.event || log?.eventType || '').trim();
  return action.toLowerCase() === 'escalated';
}

export function withoutEscalatedAuditLogEntries(logs) {
  return (Array.isArray(logs) ? logs : []).filter((log) => !isEscalatedAuditLogEntry(log));
}

/** Detail tab pagination — "Page X of Y" + §8 nav controls (customer/business audit logs, etc.). */
export function AdminDetailPaginationFooter({
  currentPage,
  totalPages,
  onPrev,
  onNext,
  goToPage,
  className = '',
}) {
  if (!totalPages || totalPages < 1) return null;
  return (
    <div className={`relative flex items-center mt-6 px-4 pt-4 ${className}`.trim()}>
      <div className="absolute left-4">
        <AdminPaginationText className="text-mediumgray">
          Page {currentPage} of {totalPages}
        </AdminPaginationText>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <AdminPaginationNavButton onClick={onPrev} disabled={currentPage === 1}>
            Previous
          </AdminPaginationNavButton>
          {[...Array(totalPages)].map((_, index) => {
            const pageNum = index + 1;
            const showPage =
              pageNum === 1 ||
              pageNum === totalPages ||
              (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
            if (!showPage) {
              const prevPage = currentPage - 2;
              const nextPage = currentPage + 2;
              if (
                (pageNum === prevPage && currentPage > 3) ||
                (pageNum === nextPage && currentPage < totalPages - 2)
              ) {
                return <AdminPaginationEllipsis key={pageNum} />;
              }
              return null;
            }
            return (
              <AdminPaginationNavButton
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                active={currentPage === pageNum}
              >
                {pageNum}
              </AdminPaginationNavButton>
            );
          })}
          <AdminPaginationNavButton onClick={onNext} disabled={currentPage === totalPages}>
            Next
          </AdminPaginationNavButton>
        </div>
      </div>
    </div>
  );
}

/* ── Admin / Dashboard fields (Field System compact layer) ─── */
/* Presets: default (352px), inline-{96–240}, search, page, upload, display, payment, verification. */

function fieldStateClasses(isView, hasError, interactive = 'hover:border-primaryblue') {
  const state = isView
    ? 'bg-gray-100 text-mediumgray cursor-not-allowed'
    : interactive;
  const error = hasError ? ' !border-alertred' : '';
  return `${state}${error}`;
}

/** Two-column form grid; a lone field or odd-last field spans the full row on sm+. */
export const adminFormTwoColGridClass =
  'grid w-full grid-cols-1 sm:grid-cols-2 gap-4 [&>*]:min-w-0 [&>*:last-child:nth-child(odd)]:sm:col-span-2 [&>*:only-child]:sm:col-span-2';

export function adminFormFieldClassNames(preset = 'default', extra = '') {
  return `${dashboardFieldClassNames(preset)} ${extra}`.trim();
}

/** Input Field — Default / medium (38px total incl. border + pad · border-box). Legacy `medium` maps to default. */
export function adminFormInputClasses(isView = false, hasError = false, preset = 'default') {
  return `${adminFormFieldClassNames(preset, fieldStateClasses(isView, hasError))}`.trim();
}

/** Input Field — Inline width preset (96, 120, 140, 160, 240). */
export function adminInlineInputClasses(preset = '140', isView = false, hasError = false) {
  return `${dashboardInlineFieldClassNames(preset, fieldStateClasses(isView, hasError))}`.trim();
}

/** Payment Input — Default or inline preset (expiry 120, CVV 96, etc.). */
export function adminPaymentInputClasses(isView = false, hasError = false, preset = 'default') {
  return `${dashboardPaymentFieldClassNames(preset, fieldStateClasses(isView, hasError))}`.trim();
}

/** Display Field — read-only value shell (not a disabled input). */
export function adminDisplayFieldClasses(extra = '') {
  return `${dashboardFieldClassNames('display')} text-offblack ${extra}`.trim();
}

/** Verification Code Input — single digit box. */
export function adminVerificationDigitClasses(hasError = false) {
  return `${dashboardVerificationDigitClassNames(fieldStateClasses(false, hasError))}`.trim();
}

/** Verification Code Input — centred digit group (4 or 6). */
export function adminVerificationCodeGroupClassNames(digits = 6) {
  return dashboardVerificationGroupClassNames(digits);
}

/** Native select — Input Field Default. */
export function adminFormSelectClasses(isView = false, hasError = false, preset = 'default') {
  return `${adminFormFieldClassNames(
    preset,
    fieldStateClasses(isView, hasError, 'cursor-pointer hover:border-primaryblue') +
      ' appearance-none pr-10'
  )}`.trim();
}

/** Page Field — long text (notes, description). */
export function adminFormTextareaClasses(isView = false, hasError = false) {
  return `${adminFormFieldClassNames(
    'page',
    fieldStateClasses(isView, hasError, 'resize-y hover:border-primaryblue')
  )}`.trim();
}

/** Search Field — toolbar / list search. */
export function adminSearchFieldClasses(extra = '') {
  return `${dashboardFieldClassNames('search')} hover:border-primaryblue ${extra}`.trim();
}

/** Upload Area shell. */
export function adminUploadFieldClasses(extra = '') {
  return `${dashboardFieldClassNames('upload')} ${extra}`.trim();
}

/** Full width inside narrow detail columns (config editors, wizards). Width-only; tokens unchanged. */
export const DASHBOARD_DETAIL_FIELD_WIDTH = '!max-w-full !min-w-0 w-full';

/** Input Field Default — full column width. */
export function adminFormDetailInputClasses(isView = false, hasError = false) {
  return `${adminFormInputClasses(isView, hasError, 'default')} ${DASHBOARD_DETAIL_FIELD_WIDTH}`.trim();
}

/** Select — full column width. */
export function adminFormDetailSelectClasses(isView = false, hasError = false) {
  return `${adminFormSelectClasses(isView, hasError, 'default')} ${DASHBOARD_DETAIL_FIELD_WIDTH} appearance-none pr-12`.trim();
}

/** Page Field — full column width. */
export function adminFormDetailTextareaClasses(isView = false, hasError = false) {
  return `${adminFormTextareaClasses(isView, hasError)} ${DASHBOARD_DETAIL_FIELD_WIDTH}`.trim();
}

/** Custom dropdown trigger — Input Field Default, full column width. */
export function adminFormDetailDropdownTriggerClass(isView = false, hasError = false, extra = '') {
  const state = isView
    ? 'bg-gray-100 text-mediumgray cursor-not-allowed'
    : 'bg-white hover:border-primaryblue cursor-pointer';
  return [
    dashboardFieldClassNames('default'),
    DASHBOARD_DETAIL_FIELD_WIDTH,
    'text-left flex items-center justify-between gap-2 pr-12',
    state,
    hasError ? '!border-alertred' : '',
    extra,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
}

const DASHBOARD_WIZARD_FIELD_WIDTH = DASHBOARD_DETAIL_FIELD_WIDTH;

export function adminWizardInputClasses(hasError = false) {
  return `${adminFormInputClasses(false, hasError, 'default')} ${DASHBOARD_WIZARD_FIELD_WIDTH}`.trim();
}

export function adminWizardTextareaClasses(hasError = false) {
  return `${adminFormTextareaClasses(false, hasError)} ${DASHBOARD_WIZARD_FIELD_WIDTH}`.trim();
}

export function adminWizardSelectClasses(hasError = false) {
  return `${adminFormSelectClasses(false, hasError, 'default')} ${DASHBOARD_WIZARD_FIELD_WIDTH}`.trim();
}

/** PrimeReact Dropdown trigger — pass as FieldDropdown `className`. */
export function adminWizardDropdownClasses(hasError = false, muted = false) {
  const base = adminWizardSelectClasses(hasError).replace(/\bblock\b/, 'flex items-center');
  const surface = muted
    ? '!border-none !bg-mistygray field-dropdown-muted'
    : 'bg-white field-dropdown-standard';
  return `${base} field-dropdown p-dropdown-trigger-custom flex items-center !pr-10 ${surface}`.trim();
}

export function adminWizardChipsClasses(expanded = false, hasError = false) {
  return `${adminWizardInputClasses(hasError)} toggle-chip custom-chips-bg field-chips${
    expanded ? " field-chips--expanded" : ""
  }`.trim();
}

/** Upload Area — full wizard column width (128px / 144px min-height per dashboard spec). */
export function adminWizardUploadClasses(extra = "") {
  return `${adminUploadFieldClasses()} ${DASHBOARD_WIZARD_FIELD_WIDTH} ${extra}`.trim();
}

/** Inline time inputs in operating-hours schedule (120px preset). */
export function adminWizardTimeInputClasses(hasError = false) {
  return `${adminInlineInputClasses("120", false, hasError)} shrink-0`.trim();
}

/** Form label row with optional required star and info tooltip (create-post wizard). */
export function AdminFormFieldLabelRow({
  label,
  required = false,
  tooltip,
  htmlFor,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div className={`flex items-center mb-2 ${className}`.trim()}>
      <div className="flex items-center">
        <AdminDetailFieldLabel htmlFor={htmlFor} className="!mb-0">
          {label}
          {required ? <span className="text-alertred ml-0.5">*</span> : null}
        </AdminDetailFieldLabel>
        {tooltip ? (
          <div className="relative ml-1" ref={ref}>
            <button
              type="button"
              className="inline-flex items-center p-0 border-0 bg-transparent"
              aria-label={`More about ${label}`}
              onClick={() => setOpen((v) => !v)}
            >
              <i className="fi fi-rs-info text-sm text-primaryblue cursor-pointer" aria-hidden />
            </button>
            {open ? (
              <div className="w-[150px] md:w-[200px] bg-offblack text-white absolute z-10 p-3 ml-2 md:ml-5 -mt-28">
                <AdminAlertSupportingText className="!text-white">{tooltip}</AdminAlertSupportingText>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ── Detail pages (customer / business profile) ────────────── */

/** Field / input label — 11px lg · 12px xl · weight 400 (Admin Field System §5). */
export function AdminDetailFieldLabel({ children, className = '', htmlFor, as = 'label' }) {
  return (
    <DashboardFieldLabel htmlFor={htmlFor} as={as} className={`mb-1 block !text-offblack ${className}`.trim()}>
      {children}
    </DashboardFieldLabel>
  );
}

/** Validation / error under a field — 11px (Admin Field System §5). */
export function AdminFormFieldError({ children, className = '' }) {
  return <DashboardFieldError className={className}>{children}</DashboardFieldError>;
}

/** Read-only field value — main 13px / 400; `emphasis` → table body emphasis 12px / 500 (§7). */
export function AdminDetailFieldValue({ children, className = '', as = 'div', emphasis = false }) {
  if (emphasis) {
    return (
      <AdminTableBody as={as} emphasis className={className}>
        {children}
      </AdminTableBody>
    );
  }
  return (
    <AdminMainText as={as} className={className}>
      {children}
    </AdminMainText>
  );
}

/** Edit/Add locked value — readonly input shell. View stays as `AdminConfigLockedField` text. */
export function AdminReadOnlyField({ label, value, mono = false }) {
  const display = value == null || value === '' ? '—' : String(value);
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <input
        type="text"
        value={display}
        readOnly
        className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
      />
    </div>
  );
}

/** View → plain text. Add/Edit locked values → readonly field shell. */
export function AdminConfigLockedField({ label, value, mono = false, isView = false }) {
  if (isView) {
    const display = value == null || value === '' ? '—' : String(value);
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        {mono ? (
          <p className="text-offblack text-sm py-2 font-mono break-words">{display}</p>
        ) : (
          <AdminDetailFieldValue className="py-2 break-words">{display}</AdminDetailFieldValue>
        )}
      </div>
    );
  }
  return <AdminReadOnlyField label={label} value={value} mono={mono} />;
}

export function AdminTabButton({ active, children, className = '', ...rest }) {
  return (
    <button
      type="button"
      data-admin-allow-view-only=""
      data-admin-tab=""
      className={`py-3 px-1 border-b-2 whitespace-nowrap flex-shrink-0 ${
        active
          ? 'border-primaryblue text-primaryblue'
          : 'border-transparent text-offblack hover:text-offblack'
      } ${className}`.trim()}
      {...rest}
    >
      <DashboardText variant="tab" as="span">
        {children}
      </DashboardText>
    </button>
  );
}

/** Table severity — §7 table body emphasis (12px / 500), no colour coding. */
export function AdminSeverityText({ severity, children, className = '' }) {
  const text = children ?? severity ?? '—';
  return (
    <AdminTableBody emphasis className={className}>
      {text}
    </AdminTableBody>
  );
}

export function adminFlagStatusBadgeClass(status) {
  const s = status == null ? '' : String(status).trim();
  if (s === 'Open' || s === 'New') return 'bg-brightblue text-white';
  if (s === 'Under Review' || s === 'Active') return 'bg-warmyellow text-white';
  if (s === 'Decided' || s === 'Closed' || s === 'Resolved') return 'bg-mediumgray text-white';
  return 'bg-mediumgray text-white';
}

export function AdminFlagStatusBadge({ status, label, className = '' }) {
  return (
    <DashboardStatusLabel
      className={`whitespace-nowrap ${adminFlagStatusBadgeClass(status)} ${className}`.trim()}
    >
      {label ?? status ?? '—'}
    </DashboardStatusLabel>
  );
}

export function AdminDetailBanner({
  variant = 'warning',
  children,
  supporting,
  className = '',
}) {
  const boxClass =
    variant === 'alert'
      ? 'bg-mistygray border-alertred'
      : 'bg-mistygray border-warmyellow';
  const textClass = variant === 'alert' ? 'text-alertred' : 'text-warmyellow';
  return (
    <div className={`rounded border p-4 ${boxClass} ${className}`.trim()}>
      <AdminAlertText className={textClass}>{children}</AdminAlertText>
      {supporting ? (
        <AdminAlertSupportingText className={`mt-1 ${textClass}`}>
          {supporting}
        </AdminAlertSupportingText>
      ) : null}
    </div>
  );
}

/** Profile header — Avatar lg (40px per Avatar/Icon/Status spec). */
export function AdminProfileAvatar({ src, alt = '', initials, onError, className = '' }) {
  return (
    <DashboardAvatar
      size="lg"
      src={src}
      alt={alt}
      initials={initials}
      onError={onError}
      className={`bg-mistygray ${className}`.trim()}
    />
  );
}

export function AdminDetailTableHeader({ children, className = '' }) {
  return <AdminTableHeader className={className}>{children}</AdminTableHeader>;
}

export {
  ADMIN_MAIN_HEADER_OFFSET_CLASS,
  ADMIN_TAB_CONTENT_OFFSET_CLASS,
  ADMIN_TAB_PANEL_CLASS,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
  ADMIN_TAB_CONTENT_CONTAINER_MIN_CLASS,
  ADMIN_TAB_CONTENT_FULL_CLASS,
  ADMIN_TAB_CONTENT_WIDE_CLASS,
  adminDetailTabContentClassName,
  ADMIN_PAGE_TITLE_OFFSET_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TOOLBAR_OFFSET_CLASS,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_OFFSET_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_OFFSET_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_SECTION_OFFSET_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
  ADMIN_CONTENT_PANEL_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_COMPACT_FORM_SHELL_CLASS,
  ADMIN_COMPACT_FORM_BODY_CLASS,
  ADMIN_FORM_FOOTER_CLASS,
  adminMainHeaderOffsetClass,
  adminTabContentOffsetClass,
  adminPageTitleOffsetClass,
  adminToolbarOffsetClass,
  adminTableOffsetClass,
  adminDetailMainHeaderOffsetClass,
  adminSectionOffsetClass,
  adminMetricPanelOffsetClass,
  adminContentPanelOffsetClass,
  adminFormPanelOffsetClass,
} from './adminLayoutSpec';
